import {
  buildCards,
  type Card,
  type LedgerState,
  type MatchState,
  type OmenState,
  type MatchView,
  type Pcg32,
  type PlayerState,
  type PlayerView,
  type Side,
} from '../engine';

/** Card ids of one side whose location the viewer cannot see (the opponent's hand and both decks). */
export function unseenCards(v: MatchView, side: Side): string[] {
  const known = new Set<string>();
  const pv = v.players[side];
  for (const id of pv.spent) known.add(id);
  for (const id of pv.removed) known.add(id);
  for (const e of v.pile) if (e.source === 'card') known.add(e.cardId);
  if (side === v.viewer) for (const id of pv.hand ?? []) known.add(id);
  return buildCards(v.config, side)
    .map((c) => c.id)
    .filter((id) => !known.has(id));
}

/** Every card of both sides, from the public config. */
export function allCards(v: MatchView): Record<string, Card> {
  const cards: Record<string, Card> = {};
  for (const side of ['S', 'N'] as const)
    for (const c of buildCards(v.config, side)) cards[c.id] = c;
  return cards;
}

function toPlayer(pv: PlayerView, hand: string[], deck: string[]): PlayerState {
  return {
    side: pv.side,
    deck,
    hand,
    spent: pv.spent.slice(),
    removed: pv.removed.slice(),
    epitaphs: pv.epitaphs.slice(),
    epitaphPlays: pv.epitaphPlays,
    passed: pv.passed,
    passKind: pv.passKind,
    roundScore: pv.roundScore,
    totalScore: pv.totalScore,
    roundWins: pv.roundWins,
    pendingCrownBonuses: pv.pendingCrownBonuses.slice(),
    reserves: pv.reserves.slice(),
    reservesPawnedThisRound: pv.reservesPawnedThisRound.slice(),
    collateralUsed: pv.collateralUsed.slice(),
  };
}

/**
 * Builds a full, engine-valid state consistent with a view by dealing the unseen cards at random
 * (a determinization). Only the viewer's own hand and public information are kept exactly.
 */
export function determinize(
  v: MatchView,
  rng: Pcg32,
  cards: Record<string, Card> = allCards(v),
): MatchState {
  const players = {} as Record<Side, PlayerState>;
  for (const side of ['S', 'N'] as const) {
    const pv = v.players[side];
    const unseen = rng.shuffle(unseenCards(v, side));
    if (side === v.viewer) {
      players[side] = toPlayer(pv, (pv.hand ?? []).slice(), unseen.slice(0, pv.deckCount));
    } else {
      players[side] = toPlayer(
        pv,
        unseen.slice(0, pv.handCount),
        unseen.slice(pv.handCount, pv.handCount + pv.deckCount),
      );
    }
  }
  return {
    rulesVersion: v.rulesVersion,
    config: v.config,
    seats: v.seats,
    rng: {
      match: { state: '0000000000000000', inc: '0000000000000001' },
      ai: { seed: '0000000000000000' },
    },
    seq: v.seq,
    cards,
    pawns: { ...v.pawns },
    reservePawns: v.reservePawns,
    players,
    pile: v.pile.slice(),
    round: v.round,
    roundStarter: v.roundStarter,
    turn: v.turn,
    active: v.active,
    decider: v.decider,
    phase: v.phase,
    chain: v.chain
      ? {
          ...v.chain,
          links: v.chain.links.slice(),
          cardIds: v.chain.cardIds.slice(),
          landings: v.chain.landings.slice(),
          visited: v.chain.visited.slice(),
        }
      : null,
    pendingInscription: v.pendingInscription,
    pendingArrivals: v.pendingArrivals,
    pendingEpitaphChoice: v.pendingEpitaphChoice,
    // Simulated Epitaphs get ids that cannot collide with real ones.
    nextEpitaphSeq: 1_000_000,
    rounds: v.rounds.slice(),
    result: v.result,
    ...(v.ledger ? { ledger: ledgerFrom(v) } : {}),
  };
}

/**
 * The Long Night ledger of a determinized state: the view's, with a stand-in luck stream (the
 * real one is hidden, so simulated chances are just guesses) and no Omens it cannot name.
 */
function ledgerFrom(v: MatchView): LedgerState {
  const l = v.ledger as NonNullable<MatchView['ledger']>;
  return {
    ...l,
    luck: { state: '0000000000000001', inc: '0000000000000001' },
    omens: l.omens.filter((o): o is OmenState => o.kind !== 'unknown'),
  };
}
